import React, { useEffect, useState } from 'react';
import { API_URLS } from '../../../config/api';
import { Plus, Trash2, Edit2, Eye, ToggleLeft, ToggleRight, X as XIcon, Star, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { Switch } from '../../ui/switch';

export default function AdminKolaySitePackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ id: '', title: '', oldPrice: '', price: '', features: [], featureInput: '', is_active: true, is_popular: false, sort_order: 0, url: '' });
  const [featuresPreviewExpanded, setFeaturesPreviewExpanded] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URLS.ADMIN.KOLAY_SITE_PACKAGES);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch (e) { console.error('JSON parse error:', text.slice(0, 300)); return; }
      const normalized = (data.data || []).map((p) => {
        const oldPrice = p.oldPrice ?? p.old_price ?? p.original_price ?? '';
        let features = p.features;
        if (typeof features === 'string') {
          try { features = JSON.parse(features); } catch (_) { features = []; }
        }
        if (!Array.isArray(features)) features = [];
        const isActiveRaw = typeof p.is_active !== 'undefined' ? p.is_active : (p.active ?? 1);
        const is_active = typeof isActiveRaw === 'boolean' ? isActiveRaw : !!Number(isActiveRaw);
        return {
          id: p.id,
          title: p.title ?? p.name ?? '',
          oldPrice,
          price: p.price ?? '',
          features,
          is_active,
          is_popular: typeof p.is_popular !== 'undefined' ? !!p.is_popular : (typeof p.isPopular !== 'undefined' ? !!p.isPopular : (p.popular ? !!p.popular : (p.badge === 'Popüler')))
        };
      });
      setPackages(normalized);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  const openCreate = () => {
    setForm({ id: '', title: '', oldPrice: '', price: '', features: [], featureInput: '', is_active: true, is_popular: false, sort_order: 0, url: '' });
    setFeaturesPreviewExpanded(false);
    setShowModal(true);
  };

  const openEdit = (p) => {
    // Normalize features to { name, included } format
    const normalizedFeatures = Array.isArray(p.features) 
      ? p.features.map(f => {
          if (typeof f === 'string') {
            return { name: f, included: true };
          } else if (f && typeof f === 'object') {
            return { 
              name: f.name ?? JSON.stringify(f), 
              included: f.included !== false 
            };
          }
          return { name: String(f), included: true };
        })
      : [];
    
    setForm({
      id: p.id,
      title: p.title || '',
      oldPrice: p.oldPrice || '',
      price: p.price || '',
      features: normalizedFeatures,
      featureInput: '',
      is_active: !!p.is_active,
      is_popular: !!p.is_popular,
      sort_order: typeof p.sort_order === 'number' ? p.sort_order : parseInt(p.sort_order || 0, 10),
      url: p.url || ''
    });
    setFeaturesPreviewExpanded(false);
    setShowModal(true);
  };

  const savePackage = async () => {
    const payload = {
      id: form.id,
      title: form.title,
      // Backward-compatible fields for legacy handler
      name: form.title,
      original_price: form.oldPrice,
      oldPrice: form.oldPrice,
      price: form.price,
      slug: (form.title || '').toString().toLowerCase().trim().replace(/[^a-z0-9çğıöşü\s-]/gi,'').replace(/\s+/g,'-').replace(/ç/g,'c').replace(/ğ/g,'g').replace(/ı/g,'i').replace(/ö/g,'o').replace(/ş/g,'s').replace(/ü/g,'u'),
      description: '',
      discount: null,
      features: (form.features || []).map(f => {
        if (typeof f === 'string') {
          return { name: f, included: true };
        } else if (f && typeof f === 'object') {
          return { 
            name: f.name ?? String(f), 
            included: f.included !== false 
          };
        }
        return { name: String(f), included: true };
      }).filter(f => f.name),
      is_active: form.is_active,
      is_popular: form.is_popular,
      sort_order: Number.isFinite(form.sort_order) ? form.sort_order : parseInt(form.sort_order || 0, 10),
      url: form.url || ''
    };
    // Append id as query param for compatibility with legacy/update handlers
    const url = form.id
      ? `${API_URLS.ADMIN.UPDATE_KOLAY_SITE_PACKAGE}&id=${encodeURIComponent(form.id)}`
      : API_URLS.ADMIN.CREATE_KOLAY_SITE_PACKAGE;
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch (e) { alert(`Sunucudan geçersiz yanıt alındı: ${text.slice(0, 300)}`); return; }
      if (res.ok && (result.status === 'success' || result.ok)) {
        setShowModal(false);
        fetchPackages();
      } else {
        alert(result.message || 'Güncelleme başarısız oldu');
      }
    } catch (err) {
      alert('Bağlantı hatası, lütfen tekrar deneyin');
    }
  };

  const addFeature = () => {
    const val = form.featureInput.trim();
    if (!val) return;
    setForm(prev => ({ 
      ...prev, 
      features: [...(prev.features || []), { name: val, included: true }], 
      featureInput: '' 
    }));
  };

  const removeFeature = (idx) => {
    setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  };

  const toggleFeatureIncluded = (idx) => {
    setForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => 
        i === idx ? { ...f, included: !f.included } : f
      )
    }));
  };

  const removePackage = async (id) => {
    if (!confirm('Silmek istiyor musunuz?')) return;
    await fetch(`${API_URLS.ADMIN.DELETE_KOLAY_SITE_PACKAGE}&id=${encodeURIComponent(id)}`);
    fetchPackages();
  };

  const toggleStatus = async (id) => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.TOGGLE_KOLAY_SITE_PACKAGE_STATUS}&id=${encodeURIComponent(id)}`);
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch (_) { json = null; }
      if (!res.ok || (json && json.status !== 'success')) {
        alert((json && json.message) || 'Durum güncellenemedi');
      }
    } catch (e) {
      alert('Bağlantı hatası');
    } finally {
      fetchPackages();
    }
  };

  const togglePopular = async (id) => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.TOGGLE_KOLAY_SITE_PACKAGE_POPULAR}&id=${encodeURIComponent(id)}`);
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch (_) { json = null; }
      if (!res.ok || (json && json.status !== 'success')) {
        alert((json && json.message) || 'Popüler durumu güncellenemedi');
      }
    } catch (e) {
      alert('Bağlantı hatası');
    } finally {
      fetchPackages();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Kolay Site Paketleri</h2>
        <button onClick={openCreate} className="inline-flex items-center px-3 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" /> Yeni Paket
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Yükleniyor...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {packages.map((p) => (
            <div key={p.id} className="rounded-xl border border-gray-200 p-4 bg-white">
              <div className="flex items-start justify-between">
                <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Star className={`w-4 h-4 ${p.is_popular ? 'text-yellow-500 fill-yellow-400' : 'text-gray-300'}`} />
                  {p.title}
                  {p.is_popular && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">Popüler</span>
                  )}
                </div>
                <button onClick={() => toggleStatus(p.id)} title="Durum">
                  {p.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              <div className="mt-1 text-sm text-gray-500 line-through">{p.oldPrice}</div>
              <div className="text-2xl font-bold text-gray-900">{p.price}</div>
              <div className="mt-3 space-y-1">
                {(p.features || []).map((f, i) => {
                  const featureName = typeof f === 'string' ? f : (f && typeof f === 'object' ? (f.name ?? JSON.stringify(f)) : String(f));
                  const isIncluded = typeof f === 'object' && f !== null ? (f.included !== false) : true;
                  
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {isIncluded ? (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className={isIncluded ? 'text-gray-700' : 'text-gray-400 line-through'}>
                        {featureName}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(p)} className="inline-flex items-center px-3 h-9 rounded-lg border border-gray-300 hover:bg-gray-50"><Edit2 className="w-4 h-4 mr-2" />Düzenle</button>
                <button onClick={() => togglePopular(p.id)} className="inline-flex items-center px-3 h-9 rounded-lg border border-yellow-300 text-yellow-700 hover:bg-yellow-50"><Star className="w-4 h-4 mr-2" />Popüler</button>
                <button onClick={() => removePackage(p.id)} className="inline-flex items-center px-3 h-9 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2" />Sil</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-lg my-auto max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold break-words">{form.id ? 'Paketi Düzenle' : 'Yeni Paket'}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"><XIcon className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-700">Başlık</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-700">Eski Fiyat</label>
                  <input value={form.oldPrice} onChange={e => setForm({ ...form, oldPrice: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Fiyat</label>
                  <input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-700">URL (Paket Sayfası Linki)</label>
                <input 
                  type="text" 
                  value={form.url || ''} 
                  onChange={e => setForm({ ...form, url: e.target.value })} 
                  placeholder="/kolay-site veya https://example.com/paket"
                  className="w-full mt-1 px-3 py-2 border rounded-lg" 
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
              </div>
              <div>
                <label className="text-sm text-gray-700">Özellikler</label>
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <input value={form.featureInput} onChange={e => setForm({ ...form, featureInput: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg min-w-0" placeholder="Özellik ekle" />
                  <button type="button" onClick={addFeature} className="px-3 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700 whitespace-nowrap flex-shrink-0">Ekle</button>
                </div>
                <ul className="mt-3 space-y-2">
                  {(form.features || []).map((f, idx) => {
                    const featureName = typeof f === 'string' ? f : (f?.name ?? JSON.stringify(f));
                    const isIncluded = typeof f === 'object' && f !== null ? (f.included !== false) : true;
                    
                    return (
                      <li key={idx} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 px-3 py-2 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto min-w-0">
                          {/* Switch Toggle */}
                          <Switch 
                            checked={isIncluded}
                            onCheckedChange={() => toggleFeatureIncluded(idx)}
                            className="data-[state=checked]:bg-green-600 flex-shrink-0"
                          />
                          <span className={`text-sm flex-1 min-w-0 break-words ${isIncluded ? 'text-gray-800 font-medium' : 'text-gray-400 line-through'}`}>
                            {featureName}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isIncluded ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isIncluded ? 'Dahil' : 'Hariç'}
                          </span>
                        </div>
                        <button type="button" onClick={() => removeFeature(idx)} className="text-red-600 hover:text-red-700 flex-shrink-0 self-end sm:self-auto">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Özellik Önizleme + Toggle */}
                {(form.features && form.features.length > 0) ? (
                  <div className="mt-4 border-t pt-3">
                    <div className="text-sm font-semibold text-gray-800 mb-2">Özellik Önizleme (Kullanıcı Görünümü)</div>
                    {(form.features.slice(0, 5)).map((f, idx) => {
                      const featureName = typeof f === 'string' ? f : (f?.name ?? JSON.stringify(f));
                      const isIncluded = typeof f === 'object' && f !== null ? (f.included !== false) : true;
                      
                      return (
                        <div key={`pv-${idx}`} className="flex items-center gap-2 mb-1">
                          {isIncluded ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${isIncluded ? 'text-gray-900 font-medium' : 'text-gray-400 line-through'}`}>
                            {featureName}
                          </span>
                        </div>
                      );
                    })}
                    {form.features.length > 5 && (
                      <>
                        {featuresPreviewExpanded && (
                          <div className="mt-2 space-y-1">
                            {form.features.slice(5).map((f, idx) => {
                              const featureName = typeof f === 'string' ? f : (f?.name ?? JSON.stringify(f));
                              const isIncluded = typeof f === 'object' && f !== null ? (f.included !== false) : true;
                              
                              return (
                                <div key={`pvx-${idx}`} className="flex items-center gap-2">
                                  {isIncluded ? (
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  )}
                                  <span className={`text-sm ${isIncluded ? 'text-gray-900 font-medium' : 'text-gray-400 line-through'}`}>
                                    {featureName}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setFeaturesPreviewExpanded(v => !v)}
                          className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          {featuresPreviewExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" /> Daha Az Detay
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" /> Daha Fazla Detay
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-700">Sıralama</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value || 0, 10) })} className="w-full mt-1 px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2">
                  <input id="active" type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-green-600 flex-shrink-0" />
                  <label htmlFor="active" className="text-sm text-gray-700">Aktif</label>
                </div>
                <div className="flex items-center gap-2">
                  <input id="popular" type="checkbox" checked={form.is_popular} onChange={e => setForm({ ...form, is_popular: e.target.checked })} className="accent-black flex-shrink-0" />
                  <label htmlFor="popular" className="text-sm text-gray-900">Popüler</label>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row justify-end gap-2">
              <button onClick={() => setShowModal(false)} className="w-full sm:w-auto px-4 h-10 rounded-lg border border-gray-300">İptal</button>
              <button onClick={savePackage} className="w-full sm:w-auto px-4 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


